import React, { useState, useEffect, useRef } from "react"
import styles from "./landing.module.scss"

const PRESETS = {
  adp: "💰 Engineered systems saving ADP $6M+ annually (auto-logout & chatbot logic transition). Led callback scheduling.",
  progressmates:
    "🏗️ Founded ProgressMates: a construction ERP and financial analytics SaaS for real-time RA billing & stock inventory.",
  tech: "⚡ Expert in React, Node.js, TypeScript, Next.js, Redis, MongoDB, GraphQL, and AWS/GCP cloud environments.",
  status:
    "📍 Senior Application Developer at ADP. Currently based in the United States, building next-gen web & SaaS products.",
}

function Landing() {
  const heroRef = useRef(null)
  const canvasRef = useRef(null)
  const cardRef = useRef(null)
  const [activeQuery, setActiveQuery] = useState("status")
  const [typedText, setTypedText] = useState("")

  // Dynamic Typewriter Effect for Console Output
  useEffect(() => {
    let index = 0
    let interval = null
    const fullText = PRESETS[activeQuery]

    setTypedText("")

    interval = setInterval(() => {
      if (index < fullText.length) {
        setTypedText(prev => prev + fullText.charAt(index))
        index++
      } else {
        clearInterval(interval)
      }
    }, 20)

    return () => clearInterval(interval)
  }, [activeQuery])

  // Interactive Gravitational Swarm Particle Canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    let animationFrameId

    let width = (canvas.width = window.innerWidth)
    let height = (canvas.height = window.innerHeight)

    const particles = []
    const particleCount = 90
    const mouse = {
      x: null,
      y: null,
      targetX: null,
      targetY: null,
      active: false,
      radius: 220,
    }

    // Resize handler
    const handleResize = () => {
      if (!canvas) return
      width = canvas.width = window.innerWidth
      height = canvas.height = window.innerHeight
    }
    window.addEventListener("resize", handleResize)

    // Particle class
    class Particle {
      constructor() {
        this.reset()
        // Distribute coordinates initially
        this.x = Math.random() * width
        this.y = Math.random() * height
      }

      reset() {
        this.x = Math.random() * width
        this.y = Math.random() * height
        this.vx = (Math.random() - 0.5) * 1.5
        this.vy = (Math.random() - 0.5) * 1.5
        this.radius = Math.random() * 2 + 1
        this.speed = 0.005 + Math.random() * 0.015
        this.orbitDirection = Math.random() > 0.5 ? 1 : -1
        this.wobblePhase = Math.random() * Math.PI * 2
        this.baseColor =
          Math.random() > 0.4
            ? "rgba(99, 102, 241, 0.35)" // Indigo
            : "rgba(168, 85, 247, 0.35)" // Purple
      }

      draw() {
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2)
        ctx.fillStyle = this.baseColor
        ctx.fill()
      }

      update() {
        // Find pull targets: mouse if active, otherwise the screen center
        const targetX = mouse.active && mouse.x !== null ? mouse.x : width / 2
        const targetY = mouse.active && mouse.y !== null ? mouse.y : height / 2

        const dx = targetX - this.x
        const dy = targetY - this.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (mouse.active && dist < mouse.radius) {
          // Gravity pull + orbital swirl around cursor
          const force = (mouse.radius - dist) / mouse.radius
          this.vx +=
            (dx / dist) * force * 0.12 -
            (dy / dist) * this.orbitDirection * force * 0.3
          this.vy +=
            (dy / dist) * force * 0.12 +
            (dx / dist) * this.orbitDirection * force * 0.3
        } else {
          // Gentle ambient float
          this.vx += (dx / dist) * 0.003
          this.vy += (dy / dist) * 0.003
        }

        // Apply friction
        this.vx *= 0.97
        this.vy *= 0.97

        this.x += this.vx
        this.y += this.vy

        // Wrap around boundaries
        if (this.x < -20) this.x = width + 20
        if (this.x > width + 20) this.x = -20
        if (this.y < -20) this.y = height + 20
        if (this.y > height + 20) this.y = -20
      }
    }

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle())
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, width, height)

      const isLightTheme =
        typeof document !== "undefined" &&
        document.documentElement.classList.contains("light-theme")

      // Connective constellation lines
      for (let i = 0; i < particles.length; i++) {
        particles[i].update()
        particles[i].draw()

        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x
          const dy = particles[i].y - particles[j].y
          const dist = Math.sqrt(dx * dx + dy * dy)

          if (dist < 130) {
            const alpha = ((130 - dist) / 130) * 0.16
            ctx.beginPath()
            ctx.moveTo(particles[i].x, particles[i].y)
            ctx.lineTo(particles[j].x, particles[j].y)

            if (isLightTheme) {
              ctx.strokeStyle = `rgba(79, 70, 229, ${alpha * 0.8})` // Darker indigo lines in light mode
            } else {
              ctx.strokeStyle = `rgba(168, 85, 247, ${alpha})` // Purple neon lines in dark mode
            }

            ctx.lineWidth = 0.6
            ctx.stroke()
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate)
    }

    animate()

    // Track mouse movements
    const handleMouseMove = e => {
      const rect = canvas.getBoundingClientRect()
      mouse.x = e.clientX - rect.left
      mouse.y = e.clientY - rect.top
      mouse.active = true
    }

    const handleMouseLeave = () => {
      mouse.active = false
      mouse.x = null
      mouse.y = null
    }

    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseleave", handleMouseLeave)

    return () => {
      window.removeEventListener("resize", handleResize)
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseleave", handleMouseLeave)
      cancelAnimationFrame(animationFrameId)
    }
  }, [])

  // 3D Parallax Tilt Card Effect
  const handleMouseMove3D = e => {
    if (!cardRef.current) return
    const card = cardRef.current
    const rect = card.getBoundingClientRect()

    // Relative coordinates from card center
    const x = e.clientX - rect.left - rect.width / 2
    const y = e.clientY - rect.top - rect.height / 2

    // Tilt degrees (limit max rotation)
    const rotateX = -(y / (rect.height / 2)) * 14
    const rotateY = (x / (rect.width / 2)) * 14

    card.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.03, 1.03, 1.03)`
  }

  const handleMouseLeave3D = () => {
    if (!cardRef.current) return
    cardRef.current.style.transform =
      "rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)"
  }

  return (
    <div className={styles.hero} ref={heroRef}>
      {/* Interactive WebGL-like Canvas Grid */}
      <canvas className={styles.canvas} ref={canvasRef} />
      <div className={styles.ambientGlow} />

      <div className={styles.container}>
        {/* Left Side: 3D interactive Parallax Card with Sci-Fi HUD overlay */}
        <div
          className={styles.cardContainer}
          onMouseMove={handleMouseMove3D}
          onMouseLeave={handleMouseLeave3D}
        >
          <div className={styles.interactiveCard} ref={cardRef}>
            <div className={styles.cardGrid} />
            <div className={styles.cornerTL} />
            <div className={styles.cornerTR} />
            <div className={styles.cornerBL} />
            <div className={styles.cornerBR} />

            <div className={styles.badge}>
              <span className={styles.pulseDot} />
              Senior Developer @ ADP
            </div>
            <h1 className={styles.name}>Abhay Sharma</h1>
            <p className={styles.tagline}>Full-Stack Engineer & SaaS Founder</p>
            <div className={styles.quickStats}>
              <div className={styles.stat}>
                <span className={styles.statVal}>8+ Yrs</span>
                <span className={styles.statLbl}>Exp</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statVal}>$6M+</span>
                <span className={styles.statLbl}>Saved</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statVal}>Progressmates</span>
                <span className={styles.statLbl}>Founder</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Interactive Query Terminal Console */}
        <div className={styles.consoleSection}>
          <div className={styles.consoleHeader}>
            <span className={styles.dotRed} />
            <span className={styles.dotYellow} />
            <span className={styles.dotGreen} />
            <span className={styles.consoleTitle}>abhay_sharma_console.sh</span>
          </div>

          <div className={styles.consoleScreen}>
            <div className={styles.consoleChips}>
              <button
                className={`${styles.chip} ${
                  activeQuery === "status" ? styles.active : ""
                }`}
                onClick={() => setActiveQuery("status")}
              >
                $ whoami
              </button>
              <button
                className={`${styles.chip} ${
                  activeQuery === "adp" ? styles.active : ""
                }`}
                onClick={() => setActiveQuery("adp")}
              >
                $ adp_achievements
              </button>
              <button
                className={`${styles.chip} ${
                  activeQuery === "progressmates" ? styles.active : ""
                }`}
                onClick={() => setActiveQuery("progressmates")}
              >
                $ progressmates_saas
              </button>
              <button
                className={`${styles.chip} ${
                  activeQuery === "tech" ? styles.active : ""
                }`}
                onClick={() => setActiveQuery("tech")}
              >
                $ tech_stack
              </button>
            </div>

            <div className={styles.consoleBody}>
              <p className={styles.textOutput}>
                {typedText}
                <span className={styles.cursor}>_</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.scrollDown}>
        <div className={styles.scrollDownIcon} />
      </div>
    </div>
  )
}

export default Landing
